/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import {
  useDashboardMode,
  dashboardPageForMode,
} from '../../../../context/DashboardModeContext';

import {
  AvatarMedium,
  ExternalLink,
  IconClose,
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

// Phase 2 mobile-nav redesign: simple primary list for everyone. Auth
// users get Dashboard prepended. "How It Works" routes to AboutPage
// (/p/about) -- the dedicated About page is the canonical home for that
// content (see containers/AboutPage/AboutPage.js). A separate "About"
// item was removed (2026-06-30): it pointed to the same AboutPage, so it
// was a duplicate menu entry with no distinct destination. "Browse" goes
// straight to the search results page; the /categories interstitial was
// removed in Stream 3 PR-5 (2026-05-15) — homepage CategoryGridSection is
// the category-specific entry point.
const PRIMARY_NAV = [
  { key: 'browse', text: 'Browse', name: 'SearchPage' },
  { key: 'list-your-kit', text: 'List Your Kit', name: 'VendorStarterGuidePage' },
  { key: 'how-it-works', text: 'How It Works', name: 'AboutPage' },
  { key: 'faqs', text: 'FAQs', name: 'FAQPage' },
];

const buildPrimaryNav = (isAuthenticated, currentMode) => {
  if (!isAuthenticated) return PRIMARY_NAV;
  // Mode-aware Dashboard link so the URL matches currentMode and
  // RouteModeSync no-ops on arrival (no flip/flicker).
  const dashboardLink = {
    key: 'dashboard',
    text: 'Dashboard',
    name: dashboardPageForMode(currentMode),
  };
  return [dashboardLink, ...PRIMARY_NAV];
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

const PrimaryNavLink = ({ link, currentPage }) => {
  const isCurrent = currentPage === link.name && !link.to;
  return (
    <li className={classNames(css.navigationLink, isCurrent && css.currentPage)}>
      <NamedLink name={link.name} to={link.to}>
        <span className={css.menuItemBorder} />
        {link.text}
      </NamedLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    onClose,
    showCreateListingsLink,
    config,
    intl,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const { currentMode } = useDashboardMode();

  const primaryNavItems = buildPrimaryNav(isAuthenticated, currentMode).map(link => (
    <PrimaryNavLink key={link.key} link={link} currentPage={currentPage} />
  ));

  // Operator-configured links from hosted config still appear after the
  // primary list so the marketplace operator can extend the nav without
  // touching this component.
  const customLinkItems = (customLinks || []).map((linkConfig, index) => (
    <CustomLinkComponent
      key={`${linkConfig.text}_${index}`}
      linkConfig={linkConfig}
      currentPage={currentPage}
    />
  ));

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  // Header: logo + (for unauth users) "Log in / Sign up" small link top-right
  // + close button. Keeps brand on the left and the auth affordance close
  // to where the menu opens from on the right.
  const menuHeader = (
    <div className={css.menuHeader}>
      <LinkedLogo
        layout="mobile"
        alt={intl ? intl.formatMessage({ id: 'Topbar.logoIcon' }) : 'Home'}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      <div className={css.menuHeaderRight}>
        {!isAuthenticated ? (
          <NamedLink name="LoginPage" className={css.menuHeaderAuthLink}>
            Log in / Sign up
          </NamedLink>
        ) : null}
        {onClose ? (
          <button
            className={css.closeButton}
            onClick={onClose}
            type="button"
            aria-label="Close menu"
          >
            <IconClose rootClassName={css.closeIcon} />
          </button>
        ) : null}
      </div>
    </div>
  );

  // Location selector lives at the bottom of the drawer (above the
  // createListingsLink footer affordance) per the Phase 2 brief.
  // The old "Set your location" LocationBar was removed 2026-07-02
  // (audit "location spine" P0): it wrote localStorage only and never
  // filtered results — a decoy radius UI. Real area filtering lives in
  // the search page's Location filter, now on mobile too.

  if (!isAuthenticated) {
    return (
      <nav className={css.root}>
        {menuHeader}
        <div className={css.content}>
          <ul className={css.customLinksWrapper}>
            {primaryNavItems}
            {customLinkItems}
          </ul>
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const displayName = user.attributes.profile.firstName;

  return (
    <div className={css.root}>
      {menuHeader}
      <AvatarMedium className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <ul className={css.customLinksWrapper}>
          {primaryNavItems}
          {customLinkItems}
        </ul>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
